import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { list_news, create_news, update_news, delete_news, get_news, count_news, get_news_illustration, get_news_document } from '../../api/functions';
import { useApi } from '../../api/api';
import { LoadingSpinner, AdminEndpoint } from '../../components';
import './TabContent.css';

const NewsTab = () => {
  const { t, i18n } = useTranslation();
  const params = useParams();
  const navigate = useNavigate();
  const id = params.id;
  const listNewsApi = useApi(list_news);
  const countNewsApi = useApi(count_news);
  const createNewsApi = useApi(create_news);
  const updateNewsApi = useApi(update_news);
  const deleteNewsApi = useApi(delete_news);
  const getNewsApi = useApi(get_news);
  const getNewsIllustrationApi = useApi(get_news_illustration);
  const getNewsDocumentApi = useApi(get_news_document);
  const gridRef = useRef(null);

  const [editingNews, setEditingNews] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [copyFiles, setCopyFiles] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [allNews, setAllNews] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(16);
  const [totalNewsCount, setTotalNewsCount] = useState(0);

  const BATCH_SIZE = 50;
  const ROWS_PER_PAGE = 4;

  const fetchNews = useCallback(async () => {
    const countResult = await countNewsApi.execute({
      show_private: true,
      search_query: searchQuery || undefined
    });

    if (countResult && countResult.count !== undefined) {
      setTotalNewsCount(countResult.count);
    }

    const params = {
      skip: 0,
      limit: BATCH_SIZE,
      show_private: true,
      search_query: searchQuery || ''
    };

    const data = await listNewsApi.execute(params);

    if (data) {
      setAllNews(data);
      setCurrentPage(1);
    }
  }, [searchQuery]);

  const loadMoreNews = useCallback(async () => {
    const data = await listNewsApi.execute({
      skip: allNews.length,
      limit: BATCH_SIZE,
      show_private: true,
      search_query: searchQuery || ''
    });
    if (data) {
      setAllNews([...allNews, ...data]);
    }
  }, [allNews, searchQuery]);

  useEffect(() => {
    const currentPath = window.location.pathname;
    const isCreatingPath = currentPath.endsWith('/new') || currentPath.includes('/new/');
    const isEditingPath = currentPath.includes('/edit');

    if (!isCreatingPath && !isEditingPath) {
      fetchNews();
    }
  }, [fetchNews]);

  useEffect(() => {
    const currentPath = window.location.pathname;
    const isCreatingPath = currentPath.endsWith('/new') || currentPath.includes('/new/');
    const isEditingPath = currentPath.includes('/edit');

    if (!isCreatingPath && !isEditingPath) {
      fetchNews();
    }
  }, [fetchNews]);

  useEffect(() => {
    const calculateColumns = () => {
      if (gridRef.current) {
        const containerWidth = gridRef.current.offsetWidth;
        const minCardWidth = 280;
        const gap = 24;
        const columns = Math.max(1, Math.floor((containerWidth + gap) / (minCardWidth + gap)));
        const newItemsPerPage = ROWS_PER_PAGE * columns;
        setItemsPerPage(newItemsPerPage);
      }
    };

    calculateColumns();
    window.addEventListener('resize', calculateColumns);
    return () => window.removeEventListener('resize', calculateColumns);
  }, []);

  useEffect(() => {
    const neededItems = currentPage * itemsPerPage;
    const hasMore = allNews.length < totalNewsCount;
    if (neededItems > allNews.length && hasMore && !listNewsApi.loading) {
      loadMoreNews();
    }
  }, [currentPage, allNews.length, totalNewsCount, listNewsApi.loading, itemsPerPage]);

  useEffect(() => {
    const calculatedTotalPages = Math.ceil(totalNewsCount / itemsPerPage);
    if (totalNewsCount > 0 && calculatedTotalPages > 0 && currentPage > calculatedTotalPages) {
      setCurrentPage(calculatedTotalPages);
    } else if (totalNewsCount === 0 && currentPage !== 1) {
      setCurrentPage(1);
    }
  }, [totalNewsCount, itemsPerPage, currentPage]);

  useEffect(() => {
    const currentPath = window.location.pathname;
    const isCreatingPath = currentPath.endsWith('/new');

    if (isCreatingPath) {
      setIsCreating(true);
      setEditingNews(null);
    } else if (id && currentPath.includes('/edit')) {
      setCopyFiles({});
      const fetchNewsForEdit = async () => {
        const newsItem = await getNewsApi.execute({ news_id: parseInt(id) });
        if (newsItem) {
          setEditingNews(newsItem);
          setIsCreating(false);
        }
      };
      fetchNewsForEdit();
    } else {
      setIsCreating(false);
      setEditingNews(null);
      setCopyFiles({});
    }
  }, [id, params]);

  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const displayedNews = allNews.slice(startIndex, endIndex);
  const totalPages = Math.ceil(totalNewsCount / itemsPerPage);

  const handleCreate = () => {
    navigate('/admin/news/new');
  };

  const handleEdit = (newsItem) => {
    navigate(`/admin/news/${newsItem.id}/edit`);
  };

  const handleCancel = () => {
    navigate('/admin/news');
  };

  const storageKey = isCreating ? 'admin_news_create' : (id ? `admin_news_edit_${id}` : null);

  const handleCopy = async (newsItem) => {
    const copyData = {
      title_en: newsItem.title_en || '', title_cz: newsItem.title_cz || '', title_de: newsItem.title_de || '',
      text_en: newsItem.text_en || '', text_cz: newsItem.text_cz || '', text_de: newsItem.text_de || '',
      private: newsItem.private || false,
    };

    const files = {};
    if (newsItem.illustration_filename) {
      const blob = await getNewsIllustrationApi.execute({ news_id: newsItem.id });
      if (blob) files.illustration = new File([blob], newsItem.illustration_filename, { type: blob.type });
    }
    if (newsItem.document_filename) {
      const blob = await getNewsDocumentApi.execute({ news_id: newsItem.id });
      if (blob) files.document = new File([blob], newsItem.document_filename, { type: blob.type });
    }
    setCopyFiles(files);

    localStorage.setItem('admin_news_create', JSON.stringify(copyData));
    navigate('/admin/news/new');
  };

  const handleSubmit = async (formData) => {
    if (isCreating) {
      const result = await createNewsApi.execute(formData);

      if (createNewsApi.error) {
        toast.error(createNewsApi.error || 'An error occurred while creating the news article', {
          position: "bottom-right"
        });
        return;
      }

      if (result) {
        toast.success('News article created successfully!', {
          position: "bottom-right"
        });
        if (storageKey) localStorage.removeItem(storageKey);
        await fetchNews();
        navigate('/admin/news');
      }
    } else if (editingNews) {
      const result = await updateNewsApi.execute({
        ...formData,
        news_id: editingNews.id
      });

      if (updateNewsApi.error) {
        toast.error(updateNewsApi.error || 'An error occurred while updating the news article', {
          position: "bottom-right"
        });
        return;
      }

      if (result) {
        toast.success('News article updated successfully!', {
          position: "bottom-right"
        });
        if (storageKey) localStorage.removeItem(storageKey);
        await fetchNews();
        navigate('/admin/news');
      }
    }
  };

  const handleDelete = async (newsId) => {
    if (window.confirm('Are you sure you want to delete this news item?')) {
      const result = await deleteNewsApi.execute({ news_id: newsId });
      if (deleteNewsApi.error) {
        toast.error(deleteNewsApi.error || 'An error occurred while deleting the news article', {
          position: "bottom-right"
        });
        return;
      }

      if (result) {
        toast.success('News article deleted successfully!', {
          position: "bottom-right"
        });
        await fetchNews();
        navigate('/admin/news');
      }
    }
  };

  const getLocalizedField = (newsItem, fieldPrefix) => {
    const language = i18n.language;
    if (newsItem[`${fieldPrefix}_${language}`]) {
      return newsItem[`${fieldPrefix}_${language}`];
    }
    if (newsItem[`${fieldPrefix}_cz`]) {
      return newsItem[`${fieldPrefix}_cz`];
    }
    if (newsItem[`${fieldPrefix}_en`]) {
      return newsItem[`${fieldPrefix}_en`];
    }
    if (newsItem[`${fieldPrefix}_de`]) {
      return newsItem[`${fieldPrefix}_de`];
    }
    return '';
  };

  const newsFields = [
    {
      type: 'language_tabs',
      name: 'content_tabs',
      languages: ['en', 'cz', 'de'],
      fields: [
        { name: 'title', label: t('admin.fields.title'), type: 'text', required: false, placeholder: t('admin.placeholders.title') },
        { name: 'text', label: t('admin.fields.content'), type: 'markdown', required: false, placeholder: t('admin.placeholders.content') }
      ]
    },

    { name: 'private', label: t('admin.fields.membersOnly'), type: 'boolean', defaultValue: false },
    { name: 'illustration', label: t('admin.fields.featuredImage'), type: 'file', accept: 'image/*', currentFile: editingNews?.illustration_filename || (isCreating ? copyFiles.illustration?.name : undefined) },
    { name: 'document', label: t('admin.fields.attachment'), type: 'file', accept: '.pdf,.doc,.docx', currentFile: editingNews?.document_filename || (isCreating ? copyFiles.document?.name : undefined) }
  ];

  if (isCreating || editingNews) {
    return (
      <div className="tab-container">
        <div className="tab-header">
          <h2 className="tab-title">{isCreating ? t('admin.news.createTitle') : t('admin.news.editTitle')}</h2>
          <p className="tab-description">
            {isCreating ? t('admin.news.createDesc') : `${t('admin.news.editDesc')}: ${getLocalizedField(editingNews, 'title')}`}
          </p>
        </div>

        <div className="edit-actions-top">
          <button className="cancel-btn-top" onClick={handleCancel}>
            {t('common.cancel')}
          </button>
          {editingNews && (
            <button className="copy-btn-top" onClick={() => handleCopy(editingNews)}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="9" y="9" width="13" height="13" rx="2" ry="2" strokeLinecap="round" strokeLinejoin="round" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
              </svg>
              {t('common.copy', 'Copy')}
            </button>
          )}
          {editingNews && (
            <button
              className="delete-btn"
              onClick={() => handleDelete(editingNews.id)}
              disabled={deleteNewsApi.loading}
            >
              {deleteNewsApi.loading ? t('common.deleting') : t('admin.news.delete')}
            </button>
          )}
        </div>

        <AdminEndpoint
          fields={newsFields}
          onSubmit={handleSubmit}
          onCancel={null}
          initialData={isCreating ? { ...copyFiles } : (editingNews || {})}
          submitLabel={isCreating ? t('admin.news.createBtn') : t('admin.news.updateBtn')}
          isLoading={createNewsApi.loading || updateNewsApi.loading}
          storageKey={storageKey}
        />
      </div>
    );
  }

  return (
    <div className="tab-container">
      <div className="tab-header">
        <div>
          <h2 className="tab-title">{t('admin.news.title')}</h2>
        </div>
        <button className="create-btn" onClick={handleCreate}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
          </svg>
          {t('admin.news.createBtn')}
        </button>
      </div>

      <div className="search-section">
        <input
          type="text"
          className="search-input"
          placeholder={t('admin.news.searchPlaceholder')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      {listNewsApi.loading && allNews.length === 0 ? (
        <LoadingSpinner message={t('common.loading', 'Loading news...')} />
      ) : allNews.length === 0 ? (
        <div className="empty-state">
          <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <p>{t('admin.news.noNews')}</p>
          <button className="create-btn-secondary" onClick={handleCreate}>
            {t('admin.news.createBtn')}
          </button>
        </div>
      ) : (
        <>
          <div className="items-grid" ref={gridRef} style={{ position: 'relative', minHeight: listNewsApi.loading ? '400px' : 'auto' }}>
            {listNewsApi.loading && allNews.length > 0 && (
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 10,
                borderRadius: '12px'
              }}>
                <LoadingSpinner message={t('common.loading', 'Loading...')} />
              </div>
            )}
            {displayedNews.map(newsItem => (
              <div key={newsItem.id} className="item-card" onClick={() => handleEdit(newsItem)}>
                <div className="item-card-body">
                  <div className="item-id">#{newsItem.id}</div>
                  <h3 className="item-title">{getLocalizedField(newsItem, 'title') || 'Untitled News'}</h3>
                  <div className="item-meta">
                    <span className="item-date">
                      {new Date(newsItem.created_at).toLocaleDateString()}
                    </span>
                    {newsItem.private && <span className="item-badge">Members Only</span>}
                  </div>
                </div>
                <div className="item-card-actions" onClick={(e) => e.stopPropagation()}>
                  <button className="item-copy-btn" onClick={() => handleCopy(newsItem)} title="Duplicate">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" strokeLinecap="round" strokeLinejoin="round" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
                    </svg>
                    Copy
                  </button>
                  <button className="item-delete-btn" onClick={() => handleDelete(newsItem.id)} title="Delete">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline strokeLinecap="round" strokeLinejoin="round" points="3 6 5 6 21 6" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M10 11v6M14 11v6" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2" />
                    </svg>
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>

          {totalPages > 1 && (
            <div className="pagination">
              <button
                className="pagination-btn"
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
              >
                {t('common.previous')}
              </button>
              <span className="pagination-info">
                {t('common.page')} {currentPage} {t('common.of')} {totalPages}
              </span>
              <button
                className="pagination-btn"
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={currentPage >= totalPages}
              >
                {t('common.next')}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default NewsTab;                                                                  